import React, { useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import PhoneInput from 'new-ui/Components/Form/PhoneInput';
import { useTranslation } from 'utils/modifiedTranslation';
import { contactTrialForMoreInfo } from 'modules/matches/api';
import { useAsyncAction } from 'utils/hooks/useAsyncAction';
import {
  FAILURE, PENDING, SUCCESS,
} from 'utils/constants/queryStatuses';
import Button from 'new-ui/Components/Button';
import styles from './getInTouchouch.module.css';
import { ReactComponent as InfoIcon } from './images/info-circle.svg';
import { ReactComponent as EnvelopIcon } from './images/envelop.svg';
import SpinnerIcon from './images/spinner.svg';
import { SuccessMessage } from './SuccessMessage/SuccessMessage';
import {
  minLength,
  maxLength,
  required,
  validEmail,
  validPhone,
} from './validators';

const MESSAGE_MAX_LENGTH = 1000;
const NAME_MAX_LENGTH = 255;
const EMAIL_MAX_LENGTH = 255;
const PHONE_MAX_LENGTH = 15;
const NAME_MIN_LENGTH = 2;
const PHONE_MIN_LENGTH = 8;

const Spinner = () => (
  <div className={styles.spinner}>
    <img src={SpinnerIcon} alt="Loading..." />
  </div>
);

export const GetInTouch = ({ presetValues = {}, nctId, onSuccess }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    message: presetValues.message || '',
    name: presetValues.name || '',
    phone: presetValues.phone || '',
    email: presetValues.email || '',
  });

  const [contactTrialParams, setContactTrialParams] = useState(null);

  const [errors, setErrors] = useState({});

  const { status: contactTrialSendStatus } = useAsyncAction({
    action: contactTrialForMoreInfo,
    parameters: contactTrialParams,
    isActive: !!contactTrialParams,
  });

  const inputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const fields = [
    {
      name: 'message',
      label: t('get_in_touch.label.message'),
      validators: [
        maxLength(MESSAGE_MAX_LENGTH, t('get_in_touch.error.max_length', { length: MESSAGE_MAX_LENGTH })),
      ],
      element: (
        <textarea
          placeholder={t('get_in_touch.placeholder.message')}
          value={formData.message}
          onChange={(e) => inputChange('message', e.target.value)}
        />
      ),
    },
    {
      name: 'name',
      label: t('get_in_touch.label.name'),
      isRequired: true,
      validators: [
        required(t('get_in_touch.error.name.required')),
        minLength(NAME_MIN_LENGTH, t('get_in_touch.error.min_length', { length: NAME_MIN_LENGTH })),
        maxLength(NAME_MAX_LENGTH, t('get_in_touch.error.max_length', { length: NAME_MAX_LENGTH })),
      ],
      element: (
        <input
          type="text"
          placeholder={t('get_in_touch.placeholder.name')}
          value={formData.name}
          onChange={(e) => inputChange('name', e.target.value)}
        />
      ),
    },
    {
      name: 'phone',
      label: t('get_in_touch.label.phone'),
      validators: [
        minLength(PHONE_MIN_LENGTH, t('get_in_touch.error.min_length', { length: PHONE_MIN_LENGTH })),
        maxLength(PHONE_MAX_LENGTH, t('get_in_touch.error.max_length', { length: PHONE_MAX_LENGTH })),
        validPhone(t('get_in_touch.error.phone.invalid')),
      ],
      element: (
        <PhoneInput
          value={formData.phone}
          placeholder={t('get_in_touch.placeholder.phone')}
          country="us"
          theme="default"
          onChange={(newValue) => {
            const trimmedValue = newValue?.trim();
            const formattedValue = trimmedValue
              ? (trimmedValue.startsWith('+') ? trimmedValue : `+${trimmedValue}`)
              : '';
            inputChange('phone', formattedValue);
          }}
        />
      ),
    },
    {
      name: 'email',
      label: t('get_in_touch.label.email'),
      isRequired: true,
      validators: [
        required(t('get_in_touch.error.email.required')),
        validEmail(t('get_in_touch.error.email.invalid')),
        maxLength(EMAIL_MAX_LENGTH, t('get_in_touch.error.max_length', { length: EMAIL_MAX_LENGTH })),
      ],
      element: (
        <div className={styles.emailInputWrapper}>
          <input
            type="text"
            placeholder={t('get_in_touch.placeholder.email')}
            value={formData.email}
            onChange={(e) => inputChange('email', e.target.value)}
          />
          <EnvelopIcon />
        </div>
      ),
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    fields.forEach(({ name, validators, isRequired }) => {
      const value = formData[name];
      if (!isRequired && String(value).trim() === '') return;

      if (validators) {
        for (const validator of validators) {
          const result = validator(value);
          if (result !== true) {
            newErrors[name] = result;
            break;
          }
        }
      }
    });

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setContactTrialParams({
      nctNumber: nctId,
      contactEmail: formData.email,
      contactPhone: formData.phone || undefined,
      contactName: formData.name,
      message: formData.message,
    });
  };

  if (contactTrialSendStatus === SUCCESS) {
    onSuccess();
    return <SuccessMessage />;
  }

  return (
    <div className={styles.root}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <h2 className={styles.title}>
          {t('get_in_touch.title.main')}
        </h2>

        {fields.map(({
          name, label, element, isRequired,
        }) => (
          <div
            key={name}
            className={cx(styles.fieldWrapper, { [styles.invalid]: errors[name] })}
          >
            <div className={styles.labelWrapper}>
              <span className={styles.label}>{label}</span>
              {isRequired ? <span className={styles.required}>*</span> : null}
            </div>
            <div className={cx({ [styles.errorInput]: errors[name] })}>
              {element}
            </div>
            {errors[name] && (
              <div className={styles.errorText}>
                <span className={styles.errorIcon}><InfoIcon /></span>
                {errors[name]}
              </div>
            )}
          </div>
        ))}

        <div className={styles.buttonWrapper}>
          {contactTrialSendStatus === FAILURE && (<div>{t('get_in_touch.error.api')}</div>)}

          <Button
            className={styles.sendButton}
            action={handleSubmit}
            customIcon={contactTrialSendStatus === PENDING ? <Spinner /> : null}
            disabled={contactTrialSendStatus === PENDING}
            title={contactTrialSendStatus === PENDING ? null : t('get_in_touch.button.send')}
          />
        </div>
      </form>
    </div>
  );
};

GetInTouch.propTypes = {
  presetValues: PropTypes.shape({
    message: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
  nctId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
